import { OMT_DataCacheWithIO } from '../../Services/OMT/utils/OMT_DataCacheWithIO';

let _instance = null;

/**
 * Class for storing temporary editor data in local storage
 */
export class EditorStorageManager {
  /**
   * get singleton instance
   * @returns {EditorStorageManager}
   */
  static getInstance() {
    if (_instance === null) {
      _instance = new EditorStorageManager();
    }
    return _instance;
  }

  /**
   * constructor
   */
  constructor() {
    this._lastSaveSuccessful = true;
    // copy/serialize original data for restoration
    this._originalDataJSON = JSON.stringify(G.json.levels);
    this._originalOrderDataJSON = JSON.stringify(G.json.levels.order);
    this._orignalLevelDataCache = {};
    // data cache and restore local storage data
    this._dataCache = new OMT_DataCacheWithIO(`EDIT_${G.BuildEnvironment.APP_ID.toUpperCase()}`);
    // apply local changes
    this._restoreUnexportedChanges();
  }

  /**
   * restore unexported level changes from local storage
   */
  _restoreUnexportedChanges() {
    const cacheKeys = this._dataCache.getKeys();

    // restore map path changes from storage
    if (this._dataCache.hasKey('MAP_PATH')) {
      G.json.levels.order = JSON.parse(this._dataCache.getData('MAP_PATH')).order;
    }

    // restore level changes from storage
    cacheKeys.forEach((cacheKey) => {
      if (cacheKey.indexOf('LVL_') !== 0) return;
      const lvlData = JSON.parse(this._dataCache.getData(cacheKey));
      const toReplacelvlData = G.Helpers.levelDataMgr.getRawLevelById(lvlData.id);
      if (toReplacelvlData != null) { // replacing a previously exported level
        const levelIndex = G.json.levels.levels.indexOf(toReplacelvlData);
        G.json.levels.levels[levelIndex] = lvlData;
      } else { // is a new level not export
        G.json.levels.levels.push(lvlData);
      }
    });
  }

  /**
   * get a copy of the original level data
   * @return {Object}
   */
  get originalData() {
    return JSON.parse(this._originalDataJSON);
  }

  /**
   * get original level data by id
   * @param {string} lvlId
   * @returns {Object}
   */
  getOriginalLevelData(lvlId) {
    return G.Helpers.levelDataMgr.getRawLevelById(lvlId, this.originalData);
  }

  /**
   * check if a level has mofidications
   * @param {Object} lvlData
   * @returns {Boolean}
   */
  wasLevelModified(lvlData) {
    let originalLvlJSON;
    const cacheId = `lvl_${lvlData.id}`;
    // we cache this for performance boost
    if (this._orignalLevelDataCache[cacheId]) {
      originalLvlJSON = this._orignalLevelDataCache[cacheId];
    } else {
      originalLvlJSON = JSON.stringify(this.getOriginalLevelData(lvlData.id, this.originalData));
      this._orignalLevelDataCache[cacheId] = originalLvlJSON;
    }
    return JSON.stringify(lvlData) === originalLvlJSON;
  }

  /**
   * save level changes to local storage now
   * @param {string} lvlId
   * @param {Object} lvlData
   */
  saveLevelChanges(lvlId, lvlData) {
    const isModified = !this.wasLevelModified(lvlData);
    if (isModified) {
      this._dataCache.setData(`LVL_${lvlId}`, JSON.stringify(lvlData));
      this._lastSaveSuccessful = this._dataCache.writeToLocalStorage();
    } else {
      this.resetLevelChanges(lvlId);
    }
  }

  /**
   * reset changes to a level now
   * @param {string} lvlId
   * @returns {Object} copy of original level data
   */
  resetLevelChanges(lvlId) {
    this._dataCache.clearData(`LVL_${lvlId}`);
    const originalLvlDataCopy = this.getOriginalLevelData(lvlId);
    G.Helpers.levelDataMgr.replaceRawLevelById(lvlId, originalLvlDataCopy);
    this._lastSaveSuccessful = this._dataCache.writeToLocalStorage();
    return originalLvlDataCopy;
  }

  /**
   * true if path has been modified
   * @returns {boolean}
   */
  get pathModified() {
    const levelOrderJSON = JSON.stringify(G.json.levels.order);
    return this._originalOrderDataJSON !== levelOrderJSON;
  }

  /**
   * save changes to the path local storage
   */
  savePathChanges() {
    if (!this.pathModified) return;
    this._dataCache.setData('MAP_PATH', JSON.stringify({ order: G.json.levels.order }));
    this._lastSaveSuccessful = this._dataCache.writeToLocalStorage();
  }

  /**
   * true if any modifications have been made
   * @returns {boolean}
   */
  get hasModifications() {
    const keyList = this._dataCache.getKeys();
    for (let i = 0; i < keyList.length; i++) {
      const key = keyList[i];
      if (key.indexOf('LVL_') !== 0) continue;
      const levelId = key.substring(4);
      if (!this.wasLevelModified(levelId)) return true;
    }
    return this.pathModified;
  }

  /**
   * reset all level + PATH changes
   */
  resetAllChanges() {
    this._dataCache.clearAllData();
    this._dataCache.writeToLocalStorage();
  }

  /**
   * true if last save was successfull
   * @returns {boolean} success
   */
  get lastSaveSuccessfull() {
    return this._lastSaveSuccessful;
  }
}
